// File: frontend/src/pages/modlog/ModlogPage.tsx
// src/components/modlog/ModlogPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ModlogEntry, getModlogEntries } from "../../services/modlogService";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import { Pagination } from "../../utils";
import { MODLOG_PAGE_SIZE } from "../../constants/settings.constants";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./ModlogPage.css";

// Generic group — one group per unique target (match/club/team/person)
interface EntryGroup {
  key: string;        // unique group key: "match-{id}", "club-{slug}", "team-{clubSlug}/{teamSlug}", "person-{slug}", "topic-{slug}", "other"
  label: string;      // display title shown in accordion header
  url: string | null; // link to the target page
  entries: ModlogEntry[];
}

/**
 * Build a group key and label for a single modlog entry.
 * Matches grouping logic in DraftsPage — one accordion per unique target.
 */
const getEntryGroupInfo = (entry: ModlogEntry): { key: string; label: string; url: string | null } => {
  if (entry.matchId != null && entry.homeTeam && entry.awayTeam) {
    const url = generateMatchUrlFromEntry(
      entry.homeTeam,
      entry.awayTeam,
      entry.matchDate ? new Date(entry.matchDate) : new Date(),
      entry.matchId
    );
    return {
      key: `match-${entry.matchId}`,
      label: `${entry.homeTeam} - ${entry.awayTeam}`,
      url,
    };
  }
  if (entry.clubSlug && entry.clubName) {
    return {
      key: `club-${entry.clubSlug}`,
      label: entry.clubName,
      url: `/kulup/${entry.clubSlug}`,
    };
  }
  if (entry.teamClubSlug && entry.teamSlug) {
    const codeSuffix = entry.teamCode ? ` ${entry.teamCode.toUpperCase()}` : "";
    return {
      key: `team-${entry.teamClubSlug}-${entry.teamSlug}`,
      label: `${entry.teamName ?? entry.teamClubSlug}${codeSuffix}`,
      url: `/takim/${entry.teamClubSlug}/${entry.teamSlug}`,
    };
  }
  if (entry.personSlug && entry.personName) {
    return {
      key: `person-${entry.personSlug}`,
      label: entry.personName,
      url: `/kisi/${entry.personSlug}`,
    };
  }
  // Custom topic ("gündem") entries — previously missing here, which made every
  // topic entry fall through to the "other" group and render a bare "—" title
  if (entry.customTopicSlug && entry.customTopicTitle) {
    return {
      key: `topic-${entry.customTopicSlug}`,
      label: entry.customTopicTitle,
      url: `/gundem/${entry.customTopicSlug}`,
    };
  }
  // Topic title survived but the slug did not — still show the title, without a link
  if (entry.customTopicTitle) {
    return { key: `topic-title-${entry.customTopicTitle}`, label: entry.customTopicTitle, url: null };
  }
  return { key: "other", label: "—", url: null };
};

const groupEntries = (entries: ModlogEntry[]): EntryGroup[] => {
  const map = new Map<string, EntryGroup>();
  for (const entry of entries) {
    const { key, label, url } = getEntryGroupInfo(entry);
    if (!map.has(key)) {
      map.set(key, { key, label, url, entries: [] });
    }
    map.get(key)!.entries.push(entry);
  }
  return Array.from(map.values());
};

export const ModlogPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const [entries, setEntries] = useState<ModlogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Track which groups are collapsed (open by default)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const loadPage = useCallback(async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getModlogEntries(page, MODLOG_PAGE_SIZE);
      setEntries(response.content);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err) {
      setError(t("modlog_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPage(0);
  }, [loadPage]);

  // Redirect if not logged in — placed after all hooks
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Format an ISO date-time string to a readable date
  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const groups = groupEntries(entries);

  return (
    <div className="modlog-container">
      {/* Page header */}
      <div className="modlog-header">
        <div className="modlog-header-title">
          {/* Shield/scroll icon for moderation log */}
          <svg className="modlog-header-icon" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 16h2v2h-2v-2zm0-10h2v8h-2V7z"/>
          </svg>
          <h1>{t("modlog_title")}</h1>
        </div>
        {totalItems > 0 && (
          <span className="modlog-count">
            {t("modlog_entry_count", { count: totalItems })}
          </span>
        )}
      </div>

      <div className="modlog-divider" />

      {/* Info banner explaining what this page shows */}
      <div className="modlog-info-banner">
        <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
        <span>{t("modlog_info_banner")}</span>
      </div>

      {/* Error banner */}
      {error && <p className="modlog-error">{error}</p>}

      {/* Loading state */}
      {loading ? (
        <p className="modlog-placeholder">{t("loading")}</p>
      ) : entries.length === 0 ? (
        /* Empty state */
        <div className="modlog-empty-state">
          <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          <p>{t("modlog_empty_state")}</p>
        </div>
      ) : (
        /* Accordion groups by target */
        <div className="modlog-groups">
          {groups.map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
              <div key={group.key} className="modlog-group">
                {/* Group header */}
                <button
                  className="modlog-group-header"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div className="modlog-group-title">
                    {group.url ? (
                      <Link
                        to={group.url}
                        className="modlog-match-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {group.label}
                      </Link>
                    ) : (
                      <span>{group.label}</span>
                    )}
                    <span className="modlog-group-count">
                      ({group.entries.length})
                    </span>
                  </div>
                  {isCollapsed ? <FaChevronDown /> : <FaChevronUp />}
                </button>

                {/* Group entries */}
                {!isCollapsed && (
                  <div className="modlog-group-entries">
                    {group.entries.map((entry) => (
                      <div key={`${entry.id}-${entry.actionType}`} className="modlog-entry">
                        {/* Action badge */}
                        <div className="modlog-entry-badge-row">
                          <span className={`modlog-badge modlog-badge-${entry.actionType.toLowerCase()}`}>
                            {entry.actionType === "DELETED"
                              ? t("modlog_action_deleted")
                              : t("modlog_action_edited")}
                          </span>
                          <span className="modlog-action-by">
                            {t("modlog_action_by", { username: entry.actionByUsername })}
                          </span>
                          <span className="modlog-action-date">
                            {formatDate(entry.actionAt)}
                          </span>
                        </div>

                        {/* Entry content */}
                        <div className="modlog-entry-content">
                          {entry.content}
                        </div>

                        {/* Reason (if provided) */}
                        {entry.reason && (
                          <div className="modlog-reason">
                            <span className="modlog-reason-label">{t("modlog_reason")}:</span>
                            {/* Translate known system reason codes; fall back to raw text for human-written reasons */}
                            <span className="modlog-reason-text">
                              {entry.reason === "AUTO_BAN"
                                ? t("modlog_reason_auto_ban")
                                : entry.reason}
                            </span>
                          </div>
                        )}

                        {/* Original creation date */}
                        <div className="modlog-entry-meta">
                          <span className="modlog-created-at">
                            {t("modlog_created_at")}: {formatDate(entry.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={(page: number) => loadPage(page - 1)}
        />
      )}
    </div>
  );
};